import React from 'react'
import styled from 'styled-components'
import { NavLink, useLocation } from 'react-router-dom'
import { PATIENT_NAV_ITEMS } from './navItems'
import { shouldUseMocks } from '../services/mockApi/index'

// On desktop the rail is a permanent sidebar that toggles between expanded and
// collapsed widths. On mobile (< 768px) it slides in as an overlay drawer with
// a backdrop — that's the only way to reach Practitioners / Records / Shop /
// Help / Notifications since they aren't in the bottom tab bar.
const Rail = styled.aside`
  position: fixed;
  top: ${(p) => p.theme?.layout?.topBarHeight || '64px'};
  left: 0;
  bottom: 0;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border-right: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: ${(p) => p.theme?.z?.leftRail || 25};
  overflow-y: auto;

  /* Mobile: full drawer that slides in from the left. */
  width: 280px;
  max-width: 80vw;
  transform: ${(p) => (p.$open ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 220ms ease-out;
  box-shadow: ${(p) => (p.$open ? '0 8px 24px rgba(0,0,0,0.18)' : 'none')};

  @media (min-width: 768px) {
    transform: none;
    box-shadow: none;
    width: ${(p) =>
      p.$open
        ? p.theme?.layout?.leftRailWidth || '260px'
        : p.theme?.layout?.leftRailCollapsedWidth || '76px'};
    transition: width 200ms ease-out;
  }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: ${(p) => (p.theme?.z?.leftRail || 25) - 1};
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity 220ms ease-out;
  @media (min-width: 768px) {
    display: none;
  }
`

const Item = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  border-radius: 14px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  &.active {
    background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
    color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    font-weight: 600;
  }
  &:hover:not(.active) {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const ItemIcon = styled.span`
  font-size: 18px;
  width: 28px;
  display: inline-flex;
  justify-content: center;
  flex-shrink: 0;
`

const ItemLabel = styled.span`
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 120ms ease-out;
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
`

const Spacer = styled.div`
  flex: 1;
`

const Crisis = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  border-radius: 14px;
  background: ${(p) => p.theme?.color?.urgentSoft || '#FBEAE9'};
  color: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
`

// Drawer auto-closes on mobile when the user taps anything that navigates
// away. Desktop rail stays put (collapse/expand is a deliberate user action).
function isMobileViewport() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 767px)').matches
}

export default function PatientLeftRail({ open, onClose }) {
  const location = useLocation()
  // Inflated label visibility — hidden when the desktop rail is collapsed,
  // always visible inside the mobile drawer.
  const labelsVisible = open || isMobileViewport()
  const isActive = (to, exact) =>
    exact ? location.pathname === to : location.pathname.startsWith(to)

  const closeOnMobile = () => {
    if (isMobileViewport() && onClose) onClose()
  }

  const useMocks = shouldUseMocks()
  const visibleNavItems = PATIENT_NAV_ITEMS.filter((item) => !item.mockOnly || useMocks)

  return (
    <>
      <Backdrop $open={open} onClick={() => onClose && onClose()} aria-hidden />
      <Rail $open={open}>
        {visibleNavItems.map((item) => (
          <Item
            key={item.id}
            to={item.to}
            end={item.exact}
            onClick={closeOnMobile}
            className={isActive(item.to, item.exact) ? 'active' : undefined}
          >
            <ItemIcon>{item.icon}</ItemIcon>
            <ItemLabel $visible={labelsVisible}>{item.label}</ItemLabel>
          </Item>
        ))}
        <Spacer />
        <Crisis to="/patient-home/help/crisis" onClick={closeOnMobile}>
          <ItemIcon>🆘</ItemIcon>
          <ItemLabel $visible={labelsVisible}>Crisis support</ItemLabel>
        </Crisis>
      </Rail>
    </>
  )
}
